import {
  RedoOutlineIcon,
  TrackchangesOutlineIcon,
  UndoOutlineIcon,
} from '@carboncopyco/icons';
import { forwardRef, useContext } from 'react';
import {
  useEditorDraft,
  useEditorEdits,
} from '../../context/EditorSdkProvider';
import { useEditorTool } from '../../hooks/useEditorTool';
import {
  EditorNavbarButton,
  type EditorNavbarButtonProps,
} from './EditorNavbarButton';
import { EditorNavbarContext } from './EditorNavbarContext';
import { EditorNavbarToggleButton } from './EditorNavbarToggleButton';

export const SaveButton = forwardRef<
  HTMLButtonElement,
  Pick<EditorNavbarButtonProps, 'onClick' | 'disabled'>
>(({ onClick, disabled }, ref) => {
  const draft = useEditorDraft();
  return (
    <EditorNavbarButton
      label="Save"
      ref={ref}
      onClick={onClick}
      disabled={disabled || !draft?.modified}
    />
  );
});
SaveButton.displayName = 'SaveButton';

export const PreviewButton = forwardRef<
  HTMLButtonElement,
  Pick<EditorNavbarButtonProps, 'onClick' | 'disabled'>
>(({ onClick, disabled }, ref) => {
  return (
    <EditorNavbarButton
      label="Preview"
      variant="secondary"
      ref={ref}
      onClick={onClick}
      disabled={disabled}
    />
  );
});

PreviewButton.displayName = 'PreviewButton';

export const ExitButton = forwardRef<
  HTMLButtonElement,
  Pick<EditorNavbarButtonProps, 'onClick'>
>(({ onClick }, ref) => (
  <EditorNavbarButton
    label="Exit"
    variant="tertiary"
    ref={ref}
    onClick={onClick}
    disabledWhileLoading={false}
  />
));
ExitButton.displayName = 'ExitButton';

export const RedoButton = () => {
  const { redos, redoEdit } = useEditorEdits();

  return (
    <UndoRedoButton
      tooltipMessage="Redo"
      icon={<RedoOutlineIcon />}
      onClick={redoEdit}
      disabled={!redos.length}
    />
  );
};

export const UndoButton = () => {
  const { edits, undoEdit } = useEditorEdits();

  return (
    <UndoRedoButton
      tooltipMessage="Undo"
      icon={<UndoOutlineIcon />}
      onClick={undoEdit}
      disabled={!edits.length}
    />
  );
};

const UndoRedoButton = ({
  tooltipMessage,
  icon,
  onClick,
  disabled,
}: Pick<
  EditorNavbarButtonProps,
  'tooltipMessage' | 'icon' | 'onClick' | 'disabled'
>) => {
  const { disabledUndoRedo, setDisabledUndoRedo } =
    useContext(EditorNavbarContext);

  return (
    <EditorNavbarButton
      variant="tertiary"
      tooltipMessage={tooltipMessage}
      icon={icon}
      disabled={disabledUndoRedo || disabled}
      onClick={async (event) => {
        setDisabledUndoRedo(true);
        await onClick(event);
        setDisabledUndoRedo(false);
      }}
    />
  );
};

export const ChangesTrackerButton = () => {
  const { isOpen, toggle } = useEditorTool('changeTracker');
  return (
    <EditorNavbarToggleButton
      tooltipMessage="Change History"
      icon={<TrackchangesOutlineIcon />}
      selected={isOpen}
      onSelect={toggle}
    />
  );
};
